/**
 * LandingPage — Public Read-Only Cosmic Map
 * ──────────────────────────────────────────
 * The "/" route for unauthenticated visitors. Shows a curated constellation
 * of real projects fetched from /api/showcase. Full interactivity minus
 * create/fork/edit/delete.
 *
 * What visitors CAN do:
 *   - Pan, zoom, pinch the map
 *   - Click nodes → lineage lights up
 *   - Double-click nodes → read-only ProjectDetailOverlay opens
 *   - Click connections → full chain highlights
 *   - Click challenges → see connected projects
 *   - Double-click challenges → read-only ChallengeDetailOverlay
 *   - Hover → proximity bloom, connection badges
 *
 * What visitors CANNOT do:
 *   - Create projects, fork, edit, delete
 *   - Multi-select, curate branches
 *   - Access messaging, notifications
 *
 * Route wiring (App.jsx):
 *   import LandingPage from './pages/LandingPage';
 *
 *   // Replace the current "/" redirect:
 *   <Route path="/" element={<LandingPage />} />
 *
 *   // Keep the authenticated redirect in ProtectedRoute or add:
 *   // If user is logged in and hits "/", redirect to "/dashboard"
 *
 * Place at: src/pages/LandingPage.jsx
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, ArrowRight, LogIn, UserPlus, X, Eye,
  GitFork, Zap, HelpCircle, ChevronRight,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { getProjectZone } from '../config/zones';

import CosmicMap from '../components/map/CosmicMap';
import { ProjectDetailOverlay } from '../components/overlays/ProjectDetailOverlay';
import ChallengeDetailOverlay from '../components/overlays/ChallengeDetailOverlay';

// ============================================================================
// SHOWCASE DATA HOOK
// ============================================================================

function useShowcaseData() {
  const [data, setData] = useState({ projects: [], challenges: [], connections: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const buildConnections = (projects, challenges) => {
      const connections = [];
      for (const project of projects) {
        const parentId = project.parent_project_id || project.parentProjectId;
        if (parentId) {
          connections.push({
            id: `project-${parentId}--project-${project.id}`,
            sourceId: parentId,
            targetId: project.id,
            sourceType: 'project',
            targetType: 'project',
          });
        }
        const parentIds = project.parent_project_ids || project.parentProjectIds || [];
        for (const pid of parentIds) {
          if (pid !== parentId) {
            connections.push({
              id: `project-${pid}--project-${project.id}`,
              sourceId: pid,
              targetId: project.id,
              sourceType: 'project',
              targetType: 'project',
            });
          }
        }
      }
      for (const challenge of challenges) {
        const projId = challenge.project_id || challenge.projectId;
        if (projId) {
          connections.push({
            id: `project-${projId}--challenge-${challenge.id}`,
            sourceId: projId,
            targetId: challenge.id,
            sourceType: 'project',
            targetType: 'challenge',
          });
        }
        const responseIds = challenge.response_project_ids || challenge.responseProjectIds || [];
        for (const rid of responseIds) {
          connections.push({
            id: `challenge-${challenge.id}--project-${rid}`,
            sourceId: challenge.id,
            targetId: rid,
            sourceType: 'challenge',
            targetType: 'project',
          });
        }
      }
      return connections;
    };

    const fetchShowcase = async () => {
      try {
        setLoading(true);

        // Try /api/showcase first, fall back to /api/map/projects if it fails.
        // This ensures the landing page works even if the showcase blueprint
        // isn't registered (e.g. import path mismatch on the backend).
        let projects = [];
        let challenges = [];

        try {
          const res = await api.get('/showcase');
          if (!mountedRef.current) return;
          projects = res.data?.projects || [];
          challenges = res.data?.challenges || [];
        } catch (showcaseErr) {
          // Showcase endpoint not available — try the general map endpoint
          console.warn('Showcase API unavailable, falling back to map/projects:', showcaseErr.message);
          try {
            const fallbackRes = await api.get('/map/projects');
            if (!mountedRef.current) return;
            projects = fallbackRes.data?.projects || fallbackRes.data || [];
          } catch {
            // Also try plain /projects as last resort
            try {
              const lastRes = await api.get('/projects');
              if (!mountedRef.current) return;
              projects = lastRes.data?.projects || lastRes.data || [];
            } catch {
              // All endpoints failed — show empty state, not error
              if (mountedRef.current) {
                setData({ projects: [], challenges: [], connections: [] });
                setLoading(false);
              }
              return;
            }
          }
        }

        if (!mountedRef.current) return;
        const connections = buildConnections(projects, challenges);
        setData({ projects, challenges, connections });
      } catch (err) {
        if (mountedRef.current) setError(err.message || 'Failed to load showcase');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    fetchShowcase();
    return () => { mountedRef.current = false; };
  }, []);

  return { data, loading, error };
}

// ============================================================================
// SIMPLE LINEAGE COMPUTATION (standalone, no useLineage hook needed)
// ============================================================================

function computeLineageIds(projectId, projects) {
  if (!projectId || !projects.length) return [projectId];

  const parentMap = new Map();
  const childMap = new Map();

  for (const p of projects) {
    const pids = [];
    if (p.parent_project_id) pids.push(p.parent_project_id);
    if (Array.isArray(p.parent_project_ids)) {
      for (const pid of p.parent_project_ids) {
        if (pid && !pids.includes(pid)) pids.push(pid);
      }
    }
    parentMap.set(p.id, pids);
    for (const pid of pids) {
      if (!childMap.has(pid)) childMap.set(pid, []);
      childMap.get(pid).push(p.id);
    }
  }

  const visited = new Set();
  const queue = [projectId];
  visited.add(projectId);

  // Walk up
  const walkUp = [projectId];
  while (walkUp.length) {
    const id = walkUp.pop();
    for (const pid of (parentMap.get(id) || [])) {
      if (!visited.has(pid)) {
        visited.add(pid);
        walkUp.push(pid);
      }
    }
  }

  // Walk down
  const walkDown = [projectId];
  while (walkDown.length) {
    const id = walkDown.pop();
    for (const cid of (childMap.get(id) || [])) {
      if (!visited.has(cid)) {
        visited.add(cid);
        walkDown.push(cid);
      }
    }
  }

  return Array.from(visited);
}

// ============================================================================
// CONNECTION INFO CARD (simplified version from Dashboard)
// ============================================================================

const ConnectionInfoCard = ({ source, target, onClose, onViewProject }) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  const sourceZone = getProjectZone(source);
  const targetZone = getProjectZone(target);
  const isCrossZone = sourceZone.id !== targetZone.id;
  const sourceImage = source.image_url || source.imageUrl;
  const targetImage = target.image_url || target.imageUrl;

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    timerRef.current = setTimeout(() => onClose?.(), 6000);
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleClick = (project) => {
    clearTimeout(timerRef.current);
    onViewProject?.(project);
    onClose?.();
  };

  return (
    <div
      className="absolute top-4 left-1/2 z-40 pointer-events-auto"
      style={{
        transform: `translateX(-50%) translateY(${visible ? '0' : '-12px'})`,
        opacity: visible ? 1 : 0,
        transition: 'all 0.4s cubic-bezier(0.19, 1, 0.22, 1)',
        maxWidth: '420px',
        width: 'calc(100vw - 40px)',
      }}
    >
      <div
        className="backdrop-blur-2xl border rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'rgba(9, 9, 11, 0.92)',
          borderColor: isCrossZone ? `${sourceZone.color}20` : 'rgba(63, 63, 70, 0.4)',
          boxShadow: isCrossZone
            ? `0 0 60px ${sourceZone.color}08, 0 0 60px ${targetZone.color}08, 0 20px 40px rgba(0,0,0,0.4)`
            : '0 20px 40px rgba(0,0,0,0.4)',
        }}
      >
        {isCrossZone && (
          <div className="h-[2px]" style={{ background: `linear-gradient(90deg, ${sourceZone.color}80, ${targetZone.color}80)` }} />
        )}
        <div className="p-3.5">
          <div className="flex items-center gap-2.5">
            <button onClick={() => handleClick(source)} className="flex items-center gap-2 flex-1 min-w-0 p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/30">
                {sourceImage
                  ? <img src={sourceImage} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-sm font-bold" style={{ color: sourceZone.color, backgroundColor: `${sourceZone.color}10` }}>{source.name?.[0]?.toUpperCase()}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-zinc-200 truncate">{source.name}</p>
                <p className="text-[10px] font-medium" style={{ color: sourceZone.color }}>{sourceZone.shortLabel}</p>
              </div>
            </button>
            <div className="flex-shrink-0 flex items-center">
              {isCrossZone ? (
                <div className="flex items-center gap-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sourceZone.color }} />
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: targetZone.color }} />
                </div>
              ) : (
                <ArrowRight className="w-3.5 h-3.5 text-zinc-700" />
              )}
            </div>
            <button onClick={() => handleClick(target)} className="flex items-center gap-2 flex-1 min-w-0 p-1.5 rounded-lg hover:bg-zinc-800/50 transition-colors">
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700/30">
                {targetImage
                  ? <img src={targetImage} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-sm font-bold" style={{ color: targetZone.color, backgroundColor: `${targetZone.color}10` }}>{target.name?.[0]?.toUpperCase()}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-zinc-200 truncate">{target.name}</p>
                <p className="text-[10px] font-medium" style={{ color: targetZone.color }}>{targetZone.shortLabel}</p>
              </div>
            </button>
            <button onClick={onClose} className="p-1 text-zinc-700 hover:text-zinc-400 transition-colors flex-shrink-0">
              <X className="w-3 h-3" />
            </button>
          </div>
          {isCrossZone && (
            <div className="flex items-center justify-center gap-1.5 mt-2 pt-2 border-t border-zinc-800/30">
              <Sparkles className="w-3 h-3 text-fuchsia-400/60" />
              <span className="text-[10px] text-zinc-500">
                Cross-discipline connection &mdash; <span style={{ color: sourceZone.color }}>{sourceZone.shortLabel}</span> inspired <span style={{ color: targetZone.color }}>{targetZone.shortLabel}</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Redirect logged-in users straight to dashboard
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  // ── Data ───────────────────────────────────────────────────────────
  const { data, loading, error } = useShowcaseData();

  // ── Map state (lightweight — no branches, no multi-select, no scrubber) ──
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [highlightedIds, setHighlightedIds] = useState([]);
  const [connectionInfo, setConnectionInfo] = useState(null);

  // ── Overlay state ──────────────────────────────────────────────────
  const [showProjectDetail, setShowProjectDetail] = useState(false);
  const [showChallengeDetail, setShowChallengeDetail] = useState(false);

  // ── CTA dismiss ────────────────────────────────────────────────────
  const [ctaDismissed, setCtaDismissed] = useState(false);

  // ── Lineage for selected project's overlay ─────────────────────────
  const parentProjects = useMemo(() => {
    if (!selectedProject) return [];
    const pids = [];
    if (selectedProject.parent_project_id) pids.push(selectedProject.parent_project_id);
    if (Array.isArray(selectedProject.parent_project_ids)) {
      for (const pid of selectedProject.parent_project_ids) {
        if (pid && !pids.includes(pid)) pids.push(pid);
      }
    }
    return pids
      .map(pid => data.projects.find(p => p.id === pid))
      .filter(Boolean);
  }, [selectedProject, data.projects]);

  const childProjects = useMemo(() => {
    if (!selectedProject) return [];
    return data.projects.filter(p => {
      if (p.parent_project_id === selectedProject.id) return true;
      if (Array.isArray(p.parent_project_ids) && p.parent_project_ids.includes(selectedProject.id)) return true;
      return false;
    });
  }, [selectedProject, data.projects]);

  // ── Handlers ───────────────────────────────────────────────────────

  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project);
    setSelectedChallenge(null);
    setConnectionInfo(null);
    const chain = computeLineageIds(project.id, data.projects);
    setHighlightedIds(chain);
  }, [data.projects]);

  const handleProjectDoubleClick = useCallback((project) => {
    setSelectedProject(project);
    setShowProjectDetail(true);
  }, []);

  const handleChallengeClick = useCallback((challenge) => {
    setSelectedChallenge(challenge);
    setSelectedProject(null);
    setConnectionInfo(null);
    const ids = [challenge.id];
    const parentId = challenge.project_id || challenge.projectId;
    if (parentId) ids.push(parentId);
    const responseIds = challenge.response_project_ids || [];
    if (Array.isArray(responseIds)) ids.push(...responseIds);
    setHighlightedIds(ids);
  }, []);

  const handleChallengeDoubleClick = useCallback((challenge) => {
    setSelectedChallenge(challenge);
    setShowChallengeDetail(true);
  }, []);

  const handleConnectionClick = useCallback((connection) => {
    const { sourceId, targetId, sourceType, targetType } = connection;
    if (sourceType !== 'project' || targetType !== 'project') return;
    const source = data.projects.find(p => p.id === sourceId);
    const target = data.projects.find(p => p.id === targetId);
    if (!source || !target) return;

    const combined = [...new Set([
      ...computeLineageIds(sourceId, data.projects),
      ...computeLineageIds(targetId, data.projects),
    ])];
    setHighlightedIds(combined);
    setSelectedProject(target);
    setSelectedChallenge(null);
    setConnectionInfo({ source, target });
  }, [data.projects]);

  const handleBackgroundClick = useCallback(() => {
    setSelectedProject(null);
    setSelectedChallenge(null);
    setHighlightedIds([]);
    setConnectionInfo(null);
    setShowProjectDetail(false);
    setShowChallengeDetail(false);
  }, []);

  const handleViewProject = useCallback((project) => {
    setSelectedProject(project);
    setShowProjectDetail(true);
    setHighlightedIds(computeLineageIds(project.id, data.projects));
  }, [data.projects]);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-zinc-950 overflow-hidden">
      {/* ── TOP BAR ─────────────────────────────────────────────────── */}
      <nav className="flex items-center justify-between px-4 md:px-6 h-14 border-b border-zinc-800/50 bg-zinc-950/90 backdrop-blur-xl z-50 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-base font-bold text-white tracking-tight">Enumé</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
          >
            <LogIn className="w-3.5 h-3.5" />
            Sign In
          </button>
          <button
            onClick={() => navigate('/register')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white
                       bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg shadow-purple-600/20
                       hover:shadow-purple-600/30 hover:brightness-110 active:scale-[0.97] transition-all"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Join the Cosmos</span>
            <span className="sm:hidden">Join</span>
          </button>
        </div>
      </nav>

      {/* ── MAP ─────────────────────────────────────────────────────── */}
      <main className="flex-1 relative overflow-hidden">
        {/* Loading state */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center animate-pulse shadow-lg shadow-purple-500/20">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <p className="text-sm text-zinc-500">Loading the cosmos...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-sm text-center">
              <p className="text-sm text-zinc-400 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-sm font-medium text-white bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Empty state — the first impression for a brand new platform */}
        {!loading && !error && data.projects.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10 p-4">
            {/* Subtle animated background to suggest the cosmic map that will fill this space */}
            <div className="absolute inset-0 overflow-hidden">
              {Array.from({ length: 60 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: `${1 + Math.random() * 2}px`,
                    height: `${1 + Math.random() * 2}px`,
                    opacity: 0.05 + Math.random() * 0.15,
                    animation: `twinkle ${3 + Math.random() * 4}s ease-in-out ${Math.random() * 5}s infinite`,
                  }}
                />
              ))}
            </div>

            <div className="relative text-center max-w-lg">
              {/* Animated constellation icon */}
              <div className="relative w-20 h-20 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-600/20 to-indigo-600/20 animate-pulse" />
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-purple-600/10 to-indigo-600/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-9 h-9 text-purple-400" />
                </div>
                {/* Orbiting dots suggesting future connections */}
                {['#f59e0b', '#ec4899', '#06b6d4', '#22c55e'].map((color, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: color,
                      opacity: 0.6,
                      top: '50%', left: '50%',
                      transform: `rotate(${i * 90}deg) translateX(44px) translateY(-50%)`,
                      animation: `twinkle ${2 + i * 0.5}s ease-in-out ${i * 0.3}s infinite`,
                    }}
                  />
                ))}
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 tracking-tight">
                The constellation is forming
              </h2>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed mb-3 max-w-sm mx-auto">
                Enumé maps the hidden connections between student projects. A poem that inspires a robot. A photograph that becomes a building.
              </p>
              <p className="text-xs text-zinc-500 mb-8 max-w-xs mx-auto">
                Every project you create becomes a star. Every fork becomes a connection. The map grows with you.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => navigate('/register')}
                  className="flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold text-white
                             bg-gradient-to-r from-purple-600 to-indigo-600
                             hover:brightness-110 active:scale-[0.97] transition-all
                             shadow-lg shadow-purple-600/20"
                >
                  <Sparkles className="w-4 h-4" />
                  Plant the first star
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium
                             text-zinc-400 hover:text-white border border-zinc-700/50 hover:border-zinc-600
                             transition-all"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Sign in
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Connection info card */}
        {connectionInfo && (
          <ConnectionInfoCard
            source={connectionInfo.source}
            target={connectionInfo.target}
            onClose={() => setConnectionInfo(null)}
            onViewProject={handleViewProject}
          />
        )}

        {/* THE MAP */}
        {!loading && data.projects.length > 0 && (
          <CosmicMap
            projects={data.projects}
            challenges={data.challenges}
            connections={data.connections}
            selectedProjectId={selectedProject?.id}
            selectedChallengeId={selectedChallenge?.id}
            highlightedIds={highlightedIds}
            multiSelectedIds={[]}
            activeZones={[]}
            showChallenges={true}
            isMultiSelectMode={false}
            zoom={zoom}
            pan={pan}
            onZoomChange={setZoom}
            onPanChange={setPan}
            onProjectClick={handleProjectClick}
            onProjectDoubleClick={handleProjectDoubleClick}
            onChallengeClick={handleChallengeClick}
            onChallengeDoubleClick={handleChallengeDoubleClick}
            onConnectionClick={handleConnectionClick}
            onBackgroundClick={handleBackgroundClick}
          />
        )}

        {/* ── FLOATING SIGNUP CTA ─────────────────────────────────── */}
        {!ctaDismissed && !showProjectDetail && !showChallengeDetail && !loading && data.projects.length > 0 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-32px)] max-w-lg animate-in slide-in-from-bottom-4 fade-in duration-500 delay-1000">
            <div className="relative bg-zinc-900/95 backdrop-blur-xl border border-zinc-800/60 rounded-2xl p-4 shadow-2xl">
              <button
                onClick={() => setCtaDismissed(true)}
                className="absolute top-3 right-3 p-1 text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/20">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-white mb-0.5">
                    Want to plant your own star?
                  </h3>
                  <p className="text-xs text-zinc-400 leading-relaxed mb-3">
                    Create an account to upload projects, fork ideas across disciplines, and join the constellation.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/register')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white
                                 bg-gradient-to-r from-purple-600 to-indigo-600
                                 hover:brightness-110 active:scale-[0.97] transition-all shadow-lg shadow-purple-600/15"
                    >
                      <UserPlus className="w-3 h-3" />
                      Create Account
                    </button>
                    <button
                      onClick={() => navigate('/login')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium
                                 text-zinc-400 hover:text-white border border-zinc-700/50 hover:border-zinc-600
                                 transition-all"
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── INTERACTION HINT (appears briefly, fades) ────────────── */}
        {!loading && data.projects.length > 0 && highlightedIds.length === 0 && !showProjectDetail && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/80 backdrop-blur border border-zinc-800/40 animate-in fade-in duration-700 delay-2000">
              <Eye className="w-3 h-3 text-zinc-500" />
              <span className="text-[11px] text-zinc-500">
                Click any star to see its creative lineage
              </span>
            </div>
          </div>
        )}

        {/* ── PROJECT DETAIL OVERLAY (read-only) ──────────────────── */}
        {showProjectDetail && selectedProject && (
          <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => { setShowProjectDetail(false); }}
            />
            <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl">
              <ProjectDetailOverlay
                project={selectedProject}
                parentProjects={parentProjects}
                childProjects={childProjects}
                curatedBranches={[]}
                onClose={() => setShowProjectDetail(false)}
                // Read-only: these handlers redirect to signup instead of acting
                onFork={() => navigate('/register')}
                onCreateChallenge={() => navigate('/register')}
                onEdit={null}
                onDelete={null}
                onViewProject={handleViewProject}
                onNavigate={(project) => {
                  setSelectedProject(project);
                  setHighlightedIds(computeLineageIds(project.id, data.projects));
                }}
                onViewBranch={null}
              />
            </div>
          </div>
        )}

        {/* ── CHALLENGE DETAIL OVERLAY (read-only) ────────────────── */}
        {showChallengeDetail && selectedChallenge && (
          <div className="absolute inset-0 z-40 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowChallengeDetail(false)}
            />
            <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl">
              <ChallengeDetailOverlay
                challenge={selectedChallenge}
                onClose={() => setShowChallengeDetail(false)}
                // Read-only: respond redirects to signup
                onRespond={() => navigate('/register')}
                onEdit={null}
                onDelete={null}
                onViewProject={handleViewProject}
              />
            </div>
          </div>
        )}

        {/* ── STATS BAR (bottom-left, subtle) ─────────────────────── */}
        {!loading && data.projects.length > 0 && !showProjectDetail && !showChallengeDetail && (
          <div className="absolute bottom-6 left-4 z-20 hidden md:flex items-center gap-3">
            {[
              { icon: Sparkles, label: 'projects', value: data.projects.length, color: '#a855f7' },
              { icon: GitFork, label: 'connections', value: data.connections.length, color: '#6366f1' },
              {
                icon: Zap,
                label: 'cross-zone',
                value: data.connections.filter(c => {
                  const src = data.projects.find(p => p.id === c.sourceId);
                  const tgt = data.projects.find(p => p.id === c.targetId);
                  if (!src || !tgt) return false;
                  return getProjectZone(src).id !== getProjectZone(tgt).id;
                }).length,
                color: '#ec4899',
              },
            ].map(({ icon: Icon, label, value, color }) => (
              <div
                key={label}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-900/80 backdrop-blur border border-zinc-800/30"
              >
                <Icon className="w-3 h-3" style={{ color }} />
                <span className="text-[11px] font-semibold text-zinc-300 tabular-nums">{value}</span>
                <span className="text-[10px] text-zinc-600">{label}</span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default LandingPage;
